// The single hub tint vocabulary — meaning, not form (00_CONTRACT.md §7).
//
// warm = leaking (red) · cool = fixable / calm (gray) · positive = good (emerald)
// · warn = needs a person (amber) · waiting = waiting on the client (cyan) ·
// neutral = informational, no urgency (gray chip).
//
// `warn` here is a TINT name, not the fn_now severity of the same spelling —
// severityToTint still maps severity "warn" onto `warm`, unchanged. warn / waiting
// / neutral are additive: warm / cool / positive (and cool as the default across
// the kit) keep their exact classes.
// These map onto the existing shared/tones.js physical palette (red / gray /
// emerald) — they are NOT a second palette of color values, only a meaning layer
// over the same hues. Analytics surfaces (VerdictCards, Weather) keep using
// `TONES` (emerald/amber/red/gray) directly; hub surfaces use these meaning
// tokens so two pages can never use the same hue for opposite meanings.
//
// Replaces the former `hub/_shims/tints.js` (critical/warn/watch/neutral) and
// `effort/vendor/Sentence.js`'s local (default/muted/warning) maps — the two
// competing tint vocabularies Epic 0 collapses into this one.

export const TINT = {
  warm: {
    dot: "bg-red-500",
    text: "text-red-700 dark:text-red-300",
    surface:
      "border-red-200 dark:border-red-900/50 bg-red-50/70 dark:bg-red-900/10",
  },
  cool: {
    dot: "bg-gray-400",
    text: "text-gray-800 dark:text-gray-100",
    surface: "border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900",
  },
  positive: {
    dot: "bg-emerald-500",
    text: "text-emerald-700 dark:text-emerald-300",
    surface:
      "border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/70 dark:bg-emerald-900/10",
  },
  warn: {
    dot: "bg-amber-500 dark:bg-amber-400",
    text: "text-amber-700 dark:text-amber-300",
    surface:
      "border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10",
  },
  waiting: {
    dot: "bg-cyan-500 dark:bg-cyan-400",
    text: "text-cyan-700 dark:text-cyan-300",
    surface:
      "border-cyan-200 dark:border-cyan-500/30 bg-cyan-50 dark:bg-cyan-500/10",
  },
  neutral: {
    dot: "bg-gray-400",
    text: "text-gray-600 dark:text-gray-300",
    surface:
      "border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/60",
  },
};

/**
 * Resolve a meaning tint to its class set. Unknown / missing → cool (the calm
 * default), never warm — a missing tint must never read as an alarm.
 * @param {"warm"|"cool"|"positive"|"warn"|"waiting"|"neutral"|string} [tint]
 * @returns {{dot:string,text:string,surface:string}}
 */
export const tintClasses = (tint) => TINT[tint] || TINT.cool;

// `severity` is the fn_now contract's INPUT (critical / warn / watch) — a
// severity, NOT a tint name. This maps it onto the three meaning tints: a broken
// promise (critical) and a soon-to-break one (warn) are both leaking → warm; a
// watch item is fixable → cool. Four severities collapse onto three meanings
// deliberately — the contract tints by meaning, not by severity level.
export const severityToTint = (severity) =>
  severity === "critical" || severity === "warn" ? "warm" : "cool";
// Left-rail accents for flat list rows (SituationCard `rail`): the tint as a 4px
// edge instead of a boxed surface, so a row inside a card reads as a row, not a
// card-in-card. Kept beside TINT so the two can't drift; an unknown tint is the
// neutral gray rail, never an alarm colour.
export const RAIL = {
  warm: "border-l-red-500 dark:border-l-red-400",
  warn: "border-l-amber-500 dark:border-l-amber-400",
  waiting: "border-l-cyan-500 dark:border-l-cyan-400",
  positive: "border-l-emerald-500 dark:border-l-emerald-400",
  neutral: "border-l-gray-400 dark:border-l-gray-500",
};

/**
 * Resolve a meaning tint to its left-rail class. Unknown / missing → neutral.
 * @param {string} [tint]
 * @returns {string}
 */
export const railClasses = (tint) => RAIL[tint] || RAIL.neutral;
